import React, { useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef } from "react"
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from "react-i18next"
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { Overlay } from "ol"
import { Vector as VectorSource } from "ol/source"
import { Vector as VectorLayer } from "ol/layer"
import { Fill, Style, Circle as CircleStyle, Stroke } from "ol/style"
import { easeOut } from 'ol/easing'
import { getVectorContext } from 'ol/render'
import { unByKey } from 'ol/Observable'

import useLoop from "Shared/Hooks/useLoop"

import AlertIcons from "../../components/AlertIcons"
import { getIconStyle, setLayerSource, getBaseMap } from '../../components/utils'
import '../../components/style.css'
import AdminConsoleRouter from "Pages/Main/AdminConsoleRouter"
import Map from "Shared/Components/Map"

const OverlayContent = ({ position, opened }) => {
  const { t } = useTranslation()
  const { role } = useOutletContext()

  return (
    <div style={{ overflow: "auto" }}>
      <div style={{ fontSize: "12px" }}><b>{opened["worker_name"]}</b></div>
      <div style={{ fontSize: "12px" }}>{t("smartwatch_betteryLevel")}: {opened["battery_level"]}%</div>
      <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_bodyTemp")}: ${opened["body_temp"]}°C` : t(`smartwatch_bodyTemp_${opened["body_temp"]}`)}</div>
      <div style={{ fontSize: "12px" }}>{(role === 'pm' || role === 'admin') ? `${t("smartwatch_heartRate")}: ${opened["heart_rate"]} bpm` :t(`smartwatch_heartRate_${opened["heart_rate"]}`)}</div>
      <div style={{ width: "fit-content" }}>
        {((Object.entries(opened["alerts"]).filter(([k, v]) => v === true).length > 0 === true) && opened["online"] === true) &&
          <AlertIcons data={opened["alerts"]} />
        }
      </div>
    </div>
  )
}

const MapDisplay = forwardRef(({ focus }, ref) => {
  const [opened, setOpened] = useState()

  const mapRef = useRef()
  const overlayRef = useRef()

  useLayoutEffect(() => {
    const workerLayer = new VectorLayer({
      source: new VectorSource({}),
      style: function (feature) { 
        const online = feature.get("online")
        const warning = Object.entries(feature.get("alerts")).filter(([k, v]) => v === true).length > 0

        const zIndex = online ? (warning ? 100 : 10) : 1
        const icon = online ? (warning ? "worker_warning" : "worker_normal") : "worker_offline"

        return [
          getIconStyle(icon, 30, zIndex)
        ]
      }
    })

    workerLayer.set('name', 'workerLayer')

    const zoneLayer = new VectorLayer({
      source: new VectorSource({}),
      style: function (feature) {
        return new Style({
          stroke: new Stroke({ color: 'red', width: 3 }),
          fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' })
        })
      }
    })

    zoneLayer.set('name', 'zoneLayer')

    const map = getBaseMap([zoneLayer, workerLayer])

    const overlay = new Overlay({
      element: overlayRef.current,
      offset: [25, 0],
    })

    map.addOverlay(overlay)

    map.on('click', function (e) {
      const feature_id = map.forEachFeatureAtPixel(e.pixel, function (f) {
        return f.getId()
      } , {
        layerFilter: function (layer) {
          return layer.get('name') === 'workerLayer'
        }
      })

      if (feature_id == null) {
        resetPopup()
        return
      }

      focus(feature_id, false)
    })

    mapRef.current = {
      "map": map,
      "workerLayer": workerLayer,
      "zoneLayer": zoneLayer,
      "overlay": overlay
    }
  }, [])

  const sosAnimation = () => {
    function flash(feature) {
      const map = mapRef.current.map
      let tileLayer = map.getLayers().getArray()[0]
  
      const duration = 3000
  
      const start = Date.now()
      const flashGeom = feature.getGeometry().clone()
      const listenerKey = tileLayer.on('postrender', animate)
    
      function animate(event) {
        const frameState = event.frameState
        const elapsed = frameState.time - start
        if (elapsed >= duration) {
          unByKey(listenerKey)
          return
        }

        const vectorContext = getVectorContext(event)
        const elapsedRatio = elapsed / duration
        const radius = easeOut(elapsedRatio) * 25 + 5
        const opacity = easeOut(1 - elapsedRatio)
    
        const style = new Style({
          image: new CircleStyle({
            radius: radius,
            stroke: new Stroke({
              color: 'rgba(255, 0, 0, ' + opacity + ')',
              width: 0.25 + opacity,
            }),
          }),
        })
    
        vectorContext.setStyle(style)
        vectorContext.drawGeometry(flashGeom)

        map.render()
      }
    }
    
    const map = mapRef.current.map
    const features = mapRef.current.workerLayer.getSource().getFeatures()

    features.forEach((f) => {
      if (f.get("online") && f.get("alerts")["sos"]) {
        flash(f)
      }

      map.render()
    })
  }

  const zoomToFeature = (id, feature, zoom) => {
    if (mapRef.current.selected !== id) {
      const map = mapRef.current.map

      setOpened({
        "worker_name": feature.get("worker_name"),
        "battery_level": feature.get("battery_level"),
        "body_temp": feature.get("body_temp"),
        "heart_rate": feature.get("heart_rate"),
        "online": feature.get("online"),
        "alerts": feature.get("alerts")
      })

      if ((feature.get("lon") === 0 || feature.get("lon") == null) || feature.get("lat") === 0 || feature.get("lat") == null) {      
        resetPopup()
        return
      }

      const coor = feature.getGeometry().getCoordinates()

      mapRef.current.overlay.setPosition(coor)

      if (zoom) {
        const view = map.getView()

        view.animate({
          center: coor,
          duration: 2000,
          zoom: 20
        })
      }
    }
  }

  const resetPopup = () => {
    mapRef.current.overlay.setPosition([0, 0])
    setOpened()
  }

  useLoop(sosAnimation, 2000)

  useImperativeHandle(ref, () => ({
    getFeature: (id) => {      
      return mapRef.current.workerLayer.getSource().getFeatureById(id)
    },
    zoomToFeature: (id, feature, content, zoom) => {
      zoomToFeature(id, feature, content, zoom)
    },
    updateData: (data) => {
      const map = mapRef.current.map

      if (map) {
        setLayerSource(mapRef.current.workerLayer, data)
      }
    },
    updateZone: (type, zone) => {
      const map = mapRef.current.map

      if (map) {
        switch (type) {
          case "danger_zone":
            mapRef.current.zoneLayer.setStyle(new Style({
              stroke: new Stroke({ color: 'rgb(255, 0, 0)', width: 3 }),
              fill: new Fill({ color: 'rgba(0, 255, 0, 0.1)' })
            }))

            break
          case "working_zone":
            mapRef.current.zoneLayer.setStyle(new Style({
              stroke: new Stroke({ color: 'rgb(0, 255, 0)', width: 3 }),
              fill: new Fill({ color: 'rgba(0, 255, 0, 0.1)' })
            }))

            break

          default:
            mapRef.current.zoneLayer.setStyle(new Style({
              stroke: new Stroke({ color: 'rgb(255, 247, 0)', width: 3 }),
              fill: new Fill({ color: 'rgba(255, 247, 0, 0.1)' })
            }))
            
            break
        }

        setLayerSource(mapRef.current.zoneLayer, zone)
      }
    },
    closePopup: () => {
      resetPopup()
    },
    setMapExtent: (coordinate) => {
      mapRef.current.map.getView().fit([coordinate["x1"], coordinate["y1"], coordinate["x2"], coordinate["y2"]], mapRef.current.map.getSize())
    }
  }))

  return (<>
    <Map mapRef={mapRef}/>

    <div ref={overlayRef} className="popup" style={{ padding: "10px" }}>
      {opened && <OverlayContent opened={opened}/>}
    </div>
  </>)
})

export default MapDisplay
